import * as Sentry from '@sentry/react';
import { Box } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import Main from 'components/Main/Main';
import { AnalyticsProvider } from 'contexts/Analytics/Analytics';
import { ApiProvider } from 'contexts/Api/Api';
import { ContentProvider } from 'contexts/Content/Content';
import { LaunchDarklyProvider } from 'contexts/LaunchDarkly/LaunchDarkly';
import { PageProvider } from 'contexts/Page/Page';
import { SettingsProvider } from 'contexts/Settings/Settings';
import { ThemeProvider } from 'contexts/Theme/Theme';
import { isLessonPath } from 'helpers';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { initSentry } from 'setup/sentry';

initSentry();

type ShellProps = {
    children: any;
};

const LessonMain = dynamic<ShellProps>(() => import('components/Main/LessonMain'), {
    loading: () => (
        <Main>
            <Box data-testid="lesson-shell-loading" />
        </Main>
    ),
});
const LessonRuntime = dynamic<ShellProps>(() => import('components/Main/LessonRuntime'), {
    loading: () => (
        <Main>
            <Box data-testid="lesson-runtime-loading" />
        </Main>
    ),
});

export default function App({ Component, pageProps }: AppProps) {
    const router = useRouter();
    const routePath = router.isReady ? router.asPath || router.pathname || '/' : router.pathname || '/';
    const lessonRoute = isLessonPath(routePath);
    const Shell = lessonRoute ? LessonMain : Main;
    const appErrorFallback = (
        <Main>
            <Box data-testid="app-error-fallback" role="alert" sx={{ padding: 3, textAlign: 'center' }}>
                Something went wrong. Please refresh the page.
            </Box>
        </Main>
    );
    const lessonRuntimeFallback = (
        <Main>
            <Box data-testid="lesson-runtime-fallback" role="alert" sx={{ padding: 3, textAlign: 'center' }}>
                Unable to load lesson runtime. Please refresh the page.
            </Box>
        </Main>
    );

    return (
        <LaunchDarklyProvider>
            <AnalyticsProvider>
                <ThemeProvider>
                    <SettingsProvider>
                        <ApiProvider>
                            <ContentProvider>
                                <PageProvider>
                                    <Sentry.ErrorBoundary fallback={appErrorFallback}>
                                        {lessonRoute ? (
                                            <ErrorBoundary fallback={lessonRuntimeFallback}>
                                                <LessonRuntime>
                                                    <Shell>
                                                        <Component {...pageProps} />
                                                    </Shell>
                                                </LessonRuntime>
                                            </ErrorBoundary>
                                        ) : (
                                            <Shell>
                                                <Component {...pageProps} />
                                            </Shell>
                                        )}
                                    </Sentry.ErrorBoundary>
                                </PageProvider>
                            </ContentProvider>
                        </ApiProvider>
                    </SettingsProvider>
                </ThemeProvider>
            </AnalyticsProvider>
        </LaunchDarklyProvider>
    );
}
